import { useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import moment from "moment-timezone";
import { ORDER_TYPES } from "../utils/Constants";

const useTimeServices = () => {
  const { hotel } = useSelector((state) => state.hotel);

  // Get buffer time for order type
  const getBufferTime = useCallback(
    (orderType, hotelData = hotel) => {
      if (!hotelData) return 30;
      switch (orderType) {
        case ORDER_TYPES.DELIVERY:
          return hotelData.delivery_buffer_time || 30;
        case ORDER_TYPES.PICKUP:
          return hotelData.pickup_buffer_time || 15;
        default:
          return 30;
      }
    },
    [hotel]
  );

  // Convert "HH:mm:ss" UTC string to minutes of day in hotel's local time
  const toLocalMinutes = useCallback(
    (timeStr, hotelTimezone) => {
      const localTime = moment.utc(timeStr, "HH:mm:ss").tz(hotelTimezone);
      return localTime.hours() * 60 + localTime.minutes();
    },
    []
  );

  // Check if minutes value is inside a start/stop range (supports overnight)
  const inInterval = useCallback((currentMin, startMin, stopMin) => {
    if (startMin === stopMin) return false; // closed
    return startMin < stopMin
      ? currentMin >= startMin && currentMin < stopMin
      : currentMin >= startMin || currentMin < stopMin;
  }, []);

  // Check if a date is a holiday
  const isHoliday = useCallback((date) => {
    // Check both shop_holidays and shop_hoilidays (typo in data)
    const holidays = Array.isArray(hotel?.shop_holidays)
      ? hotel.shop_holidays
      : Array.isArray(hotel?.shop_hoilidays)
      ? hotel.shop_hoilidays
      : [];
    
    if (holidays.length === 0) return false;
    
    const dateStr = moment(date).format("YYYY-MM-DD");
    return holidays.some(holiday => holiday.date === dateStr);
  }, [hotel?.shop_holidays, hotel?.shop_hoilidays]);

  // ASAP availability check
  const isAsapAvailable = useMemo(() => {
    const zone = "Asia/Kolkata"; // or hotel?.time_zone if available
  
    return (orderType) => {
      if (!hotel || !orderType || !zone) {
        return false;
      }
      
      // Check online order status
      if (hotel.online_order_active !== 1) return false;
      
      // Check hotel status
      if (hotel.hotelStatus?.[0]?.active !== 1) return false;
      
      // Check if today is a holiday
      if (isHoliday(moment())) return false;
      
      // Check order_later rules - if order_later_days >= 1, ASAP (today) is not allowed
      if (hotel.order_later === 1 && hotel.order_later_days >= 1) {
        return false;
      }

      const bufferTime = getBufferTime(orderType);
      const hotelTimezone = zone;
  
      // Current local time in hotel's timezone
      const nowLocal = moment().tz(hotelTimezone);
      const nowMinutes = nowLocal.hours() * 60 + nowLocal.minutes();
  
      // console.log("hotelTimezone", hotelTimezone);
      // console.log("nowLocal", nowLocal.format("HH:mm"));
  
      // Find today's schedule (moment: Sunday=0)
      const todayIndex = Number(nowLocal.format("d"));
      const today = hotel.workingInfo?.find((d) => d.day === todayIndex);
  
      // console.log("todayIndex", todayIndex);
      // console.log("today", today);
  
      if (!today || today.is_closed) return false;
  
      // Get order type hours for today
      const orderTypeInfo = today.hotelDayInfo?.find(
        (h) => h.type === orderType
      );
      // console.log("orderTypeInfo", orderTypeInfo);
  
      if (!orderTypeInfo || !orderTypeInfo.running_hours?.length) {
        // For DELIVERY or PICKUP, do NOT fallback to OPENING hours
        if (orderType === ORDER_TYPES.DELIVERY || orderType === ORDER_TYPES.PICKUP) {
          return false;
        }

        // For other types, optionally fallback to OPENING hours
        const openingInfo = today.hotelDayInfo?.find(
          (h) => h.type === ORDER_TYPES.OPENING
        );
        
        if (!openingInfo || !openingInfo.running_hours?.length) {
          return false;
        }
        
        // Check if current time is within opening hours
        return openingInfo.running_hours.some(({ start, stop }) => {
          const startMin = toLocalMinutes(start, hotelTimezone);
          const stopMin = toLocalMinutes(stop, hotelTimezone);
          return inInterval(nowMinutes, startMin, stopMin);
        });
      }
  
      // Check if the current time fits in any running hour slot
      return orderTypeInfo.running_hours.some(({ start, stop }) => {
        const startMin = toLocalMinutes(start, hotelTimezone);
        const stopMin = toLocalMinutes(stop, hotelTimezone);
        return inInterval(nowMinutes, startMin, stopMin);
      });
    };
  }, [hotel, getBufferTime, toLocalMinutes, inInterval, isHoliday]);
  

  // Generate ASAP label
  const getAsapLabel = useCallback(
    (orderType) => {
      if (!hotel || !orderType) {
        return {
          available: false,
          label: "ASAP : Not Available",
          bufferTime: 30,
        };
      }

      const bufferTime = getBufferTime(orderType);
      const available = isAsapAvailable(orderType);
      if (!available) {
        return {
          available: false,
          label: "ASAP : Not Available",
          bufferTime,
        };
      }

      const minTime = bufferTime;
      const maxTime = bufferTime + 5;

      return {
        available: true,
        label: `ASAP : ${minTime}-${maxTime} Mins`,
        bufferTime,
      };
    },
    [hotel, getBufferTime, isAsapAvailable]
  );

  return {
    isAsapAvailable,
    getAsapLabel,
    getBufferTime,
    hotel,
  };
};

export default useTimeServices;
